var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { SourceRecordKind } from "healthex-common/health.wallet.dtos";
import { isErr } from "healthex-common/result";
import { slugify, willNeverHappen } from "healthex-common/utils";
import { action, computed, observable, runInAction } from "mobx";
import { getObservableT } from "../Infra/i18next";
import { getHeapGlobalEventProperties } from "../Infra/LoadHeap";
import { HXMenuController } from "./HXMenu";
import { NamedRoutes } from "./NamedRoutes";
import { popNestedDialogLevel, pushNestedDialogLevel, replaceNestedDialogLevel } from "./NestedDialogHistory";
import { formatDisplayDate, procedureTypeDisplay, visitTypeDisplay } from "./PatientHistory.types";
const t = getObservableT("patients");
export class PatientSourceDetailsViewModel {
    constructor(container, sourceId, callbacks) {
        this.moreMenuController = new HXMenuController();
        this.isLoading = true;
        this.hasError = false;
        this.detail = null;
        this.isHiding = false;
        this.statDialogOpen = false;
        this.selectedStatKind = null;
        this.isLoadingStatRecords = false;
        this.statRecords = [];
        this.statFetchSequence = 0;
        this.recordDetailsDialogOpen = false;
        this.recordDetailsResourceId = null;
        this.recordDetailsRecordType = null;
        this.routingUIManager = container.routingUIManager;
        this.healthWalletService = container.healthWalletService;
        this.authManager = container.authManager;
        this.dashboardUIManager = container.patientResearchDashboardUIManager;
        this.analyticsService = container.analyticsService;
        this.sourceId = sourceId;
        this.callbacks = callbacks;
    }
    run() {
        void this.fetchDetail();
    }
    stop() {
        this.moreMenuController.closeMenu();
    }
    async fetchDetail() {
        const patientId = this.authManager.id;
        if (!patientId)
            return;
        this.isLoading = true;
        this.hasError = false;
        try {
            const detail = await this.healthWalletService.getSourceDetail(patientId, this.sourceId);
            runInAction(() => {
                this.detail = detail;
                this.isLoading = false;
            });
        }
        catch (error) {
            console.error("[PatientSourceDetails] Failed to fetch source detail", error);
            runInAction(() => {
                this.hasError = true;
                this.isLoading = false;
            });
        }
    }
    get organizationName() {
        var _a, _b;
        return (_b = (_a = this.detail) === null || _a === void 0 ? void 0 : _a.organizationName) !== null && _b !== void 0 ? _b : "";
    }
    get organizationLogoUrl() {
        var _a, _b;
        return (_b = (_a = this.detail) === null || _a === void 0 ? void 0 : _a.organizationLogoUrl) !== null && _b !== void 0 ? _b : null;
    }
    get address() {
        var _a, _b;
        return (_b = (_a = this.detail) === null || _a === void 0 ? void 0 : _a.address) !== null && _b !== void 0 ? _b : null;
    }
    get lastRefreshedDisplay() {
        var _a;
        return ((_a = this.detail) === null || _a === void 0 ? void 0 : _a.lastRefreshedAt) ? formatDisplayDate(this.detail.lastRefreshedAt) : null;
    }
    get physicianCount() {
        var _a, _b;
        return (_b = (_a = this.detail) === null || _a === void 0 ? void 0 : _a.physicianCount) !== null && _b !== void 0 ? _b : 0;
    }
    get routineVisitCount() {
        var _a, _b;
        return (_b = (_a = this.detail) === null || _a === void 0 ? void 0 : _a.routineVisitCount) !== null && _b !== void 0 ? _b : 0;
    }
    get specialistVisitCount() {
        var _a, _b;
        return (_b = (_a = this.detail) === null || _a === void 0 ? void 0 : _a.specialistVisitCount) !== null && _b !== void 0 ? _b : 0;
    }
    get unplannedVisitCount() {
        var _a, _b;
        return (_b = (_a = this.detail) === null || _a === void 0 ? void 0 : _a.unplannedVisitCount) !== null && _b !== void 0 ? _b : 0;
    }
    get diagnosticCount() {
        var _a, _b;
        return (_b = (_a = this.detail) === null || _a === void 0 ? void 0 : _a.diagnosticCount) !== null && _b !== void 0 ? _b : 0;
    }
    get visitStatCards() {
        return [
            { kind: SourceRecordKind.ROUTINE_VISITS, title: t("Routine Visits"), count: this.routineVisitCount },
            { kind: SourceRecordKind.SPECIALIST_VISITS, title: t("Specialist Visits"), count: this.specialistVisitCount },
            { kind: SourceRecordKind.UNPLANNED_VISITS, title: t("Unplanned Visit"), count: this.unplannedVisitCount },
        ].filter((card) => card.count > 0);
    }
    get hasVisitStats() {
        return this.visitStatCards.length > 0;
    }
    get hasPhysicianStats() {
        return this.physicianCount > 0;
    }
    get hasDiagnosticStats() {
        return this.diagnosticCount > 0;
    }
    get hasAnyStatCards() {
        return this.hasVisitStats || this.hasPhysicianStats || this.hasDiagnosticStats;
    }
    onRefreshSource() {
        var _a;
        this.moreMenuController.closeMenu();
        const { brandId, portalId, endpointId } = (_a = this.detail) !== null && _a !== void 0 ? _a : {};
        this.callbacks.onBeforeNavigate();
        if (brandId && portalId && endpointId) {
            this.routingUIManager.navigateTo(NamedRoutes.PATIENT_IAS_DATA_UPDATE, {}, { addRecordLocationsDirectly: "true", brandId, portalId, endpointId, refresh: "true" });
        }
        else {
            this.routingUIManager.navigateTo(NamedRoutes.PATIENT_IAS_DATA_UPDATE);
        }
    }
    async onHideSource() {
        if (this.isHiding)
            return;
        const patientId = this.authManager.id;
        if (!patientId)
            return;
        this.isHiding = true;
        try {
            const result = await this.healthWalletService.hideSource(patientId, this.sourceId);
            if (isErr(result)) {
                console.error("[PatientSourceDetails] Failed to hide source", result.error);
                return;
            }
            try {
                await this.dashboardUIManager.loadMostRecentRetrieval();
            }
            catch (error) {
                console.error("[PatientSourceDetails] Failed to refresh most recent retrieval after hiding source", error);
            }
            this.moreMenuController.closeMenu();
            this.callbacks.onHidden();
            if (!navigator.webdriver) {
                void this.analyticsService.trackHeapEvent({
                    event: "SourceHidden",
                    identity: patientId,
                    properties: { ...getHeapGlobalEventProperties(), sourceId: this.sourceId },
                });
            }
        }
        catch (error) {
            console.error("[PatientSourceDetails] Failed to hide source", error);
        }
        finally {
            runInAction(() => {
                this.isHiding = false;
            });
        }
    }
    async openStatDialog(kind) {
        if (this.statDialogOpen)
            return;
        const patientId = this.authManager.id;
        if (!patientId)
            return;
        pushNestedDialogLevel();
        this.selectedStatKind = kind;
        this.statDialogOpen = true;
        this.statRecords = [];
        this.isLoadingStatRecords = true;
        const seq = ++this.statFetchSequence;
        try {
            const records = await this.healthWalletService.getSourceRecords(patientId, this.sourceId, kind);
            if (seq !== this.statFetchSequence)
                return;
            runInAction(() => {
                this.statRecords = records;
                this.isLoadingStatRecords = false;
            });
        }
        catch (error) {
            if (seq !== this.statFetchSequence)
                return;
            console.error("[PatientSourceDetails] Failed to fetch source records", error);
            runInAction(() => {
                this.isLoadingStatRecords = false;
            });
        }
    }
    closeStatDialog(opts) {
        if (!this.statDialogOpen)
            return;
        if ((opts === null || opts === void 0 ? void 0 : opts.syncHistory) !== false)
            popNestedDialogLevel();
        this.statDialogOpen = false;
        this.selectedStatKind = null;
        this.statRecords = [];
    }
    get statDialogTitle() {
        switch (this.selectedStatKind) {
            case SourceRecordKind.ROUTINE_VISITS:
                return t("Routine Visits");
            case SourceRecordKind.SPECIALIST_VISITS:
                return t("Specialist Visits");
            case SourceRecordKind.UNPLANNED_VISITS:
                return t("Unplanned Visit");
            case SourceRecordKind.PHYSICIANS:
                return t("Physicians");
            case SourceRecordKind.DIAGNOSTIC:
                return t("Diagnostics");
            case null:
                return "";
            default:
                return willNeverHappen(this.selectedStatKind);
        }
    }
    get statDialogSubtitle() {
        switch (this.selectedStatKind) {
            case SourceRecordKind.ROUTINE_VISITS:
                return t("{{count}} Visits", { count: this.routineVisitCount });
            case SourceRecordKind.SPECIALIST_VISITS:
                return t("{{count}} Visits", { count: this.specialistVisitCount });
            case SourceRecordKind.UNPLANNED_VISITS:
                return t("{{count}} Visits", { count: this.unplannedVisitCount });
            case SourceRecordKind.DIAGNOSTIC:
                return t("{{count}} Diagnostics Events", { count: this.diagnosticCount });
            default:
                return undefined;
        }
    }
    rowLabel(record) {
        var _a;
        if (this.selectedStatKind === SourceRecordKind.PHYSICIANS) {
            return (_a = record.physicianName) !== null && _a !== void 0 ? _a : t("Physician");
        }
        if (this.selectedStatKind === SourceRecordKind.DIAGNOSTIC) {
            return record.procedureType ? procedureTypeDisplay(record.procedureType) : t("Diagnostic");
        }
        return record.visitType ? visitTypeDisplay(record.visitType) : t("Visit");
    }
    openRecordDetails(record) {
        if (!this.selectedStatKind)
            return;
        replaceNestedDialogLevel();
        this.recordDetailsResourceId = record.resourceId;
        this.recordDetailsRecordType = slugify(record.recordType);
        this.statDialogOpen = false;
        this.recordDetailsDialogOpen = true;
    }
    closeRecordDetailsDialog(opts) {
        if (!this.recordDetailsDialogOpen)
            return;
        if ((opts === null || opts === void 0 ? void 0 : opts.syncHistory) !== false)
            popNestedDialogLevel();
        this.recordDetailsDialogOpen = false;
        this.recordDetailsResourceId = null;
        this.recordDetailsRecordType = null;
    }
}
__decorate([
    observable
], PatientSourceDetailsViewModel.prototype, "isLoading", void 0);
__decorate([
    observable
], PatientSourceDetailsViewModel.prototype, "hasError", void 0);
__decorate([
    observable
], PatientSourceDetailsViewModel.prototype, "detail", void 0);
__decorate([
    observable
], PatientSourceDetailsViewModel.prototype, "isHiding", void 0);
__decorate([
    observable
], PatientSourceDetailsViewModel.prototype, "statDialogOpen", void 0);
__decorate([
    observable
], PatientSourceDetailsViewModel.prototype, "selectedStatKind", void 0);
__decorate([
    observable
], PatientSourceDetailsViewModel.prototype, "isLoadingStatRecords", void 0);
__decorate([
    observable
], PatientSourceDetailsViewModel.prototype, "statRecords", void 0);
__decorate([
    observable
], PatientSourceDetailsViewModel.prototype, "recordDetailsDialogOpen", void 0);
__decorate([
    observable
], PatientSourceDetailsViewModel.prototype, "recordDetailsResourceId", void 0);
__decorate([
    observable
], PatientSourceDetailsViewModel.prototype, "recordDetailsRecordType", void 0);
__decorate([
    action
], PatientSourceDetailsViewModel.prototype, "fetchDetail", null);
__decorate([
    computed
], PatientSourceDetailsViewModel.prototype, "organizationName", null);
__decorate([
    computed
], PatientSourceDetailsViewModel.prototype, "organizationLogoUrl", null);
__decorate([
    computed
], PatientSourceDetailsViewModel.prototype, "address", null);
__decorate([
    computed
], PatientSourceDetailsViewModel.prototype, "lastRefreshedDisplay", null);
__decorate([
    computed
], PatientSourceDetailsViewModel.prototype, "physicianCount", null);
__decorate([
    computed
], PatientSourceDetailsViewModel.prototype, "routineVisitCount", null);
__decorate([
    computed
], PatientSourceDetailsViewModel.prototype, "specialistVisitCount", null);
__decorate([
    computed
], PatientSourceDetailsViewModel.prototype, "unplannedVisitCount", null);
__decorate([
    computed
], PatientSourceDetailsViewModel.prototype, "diagnosticCount", null);
__decorate([
    computed
], PatientSourceDetailsViewModel.prototype, "visitStatCards", null);
__decorate([
    computed
], PatientSourceDetailsViewModel.prototype, "hasVisitStats", null);
__decorate([
    computed
], PatientSourceDetailsViewModel.prototype, "hasPhysicianStats", null);
__decorate([
    computed
], PatientSourceDetailsViewModel.prototype, "hasDiagnosticStats", null);
__decorate([
    computed
], PatientSourceDetailsViewModel.prototype, "hasAnyStatCards", null);
__decorate([
    action
], PatientSourceDetailsViewModel.prototype, "onRefreshSource", null);
__decorate([
    action
], PatientSourceDetailsViewModel.prototype, "onHideSource", null);
__decorate([
    action
], PatientSourceDetailsViewModel.prototype, "openStatDialog", null);
__decorate([
    action
], PatientSourceDetailsViewModel.prototype, "closeStatDialog", null);
__decorate([
    computed
], PatientSourceDetailsViewModel.prototype, "statDialogTitle", null);
__decorate([
    computed
], PatientSourceDetailsViewModel.prototype, "statDialogSubtitle", null);
__decorate([
    action
], PatientSourceDetailsViewModel.prototype, "openRecordDetails", null);
__decorate([
    action
], PatientSourceDetailsViewModel.prototype, "closeRecordDetailsDialog", null);
